import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Badge, 
  Alert, 
  Button, 
  Form, 
  InputGroup, 
  Modal,
  Accordion 
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExclamationTriangle, 
  faInfoCircle, 
  faCheckCircle, 
  faLock, 
  faUnlock, 
  faTerminal, 
  faSyncAlt, 
  faQuestionCircle, 
  faCopy, 
  faSearch, 
  faShieldAlt, 
  faExternalLinkAlt,
  faLifeRing,
  faMobileAlt,
  faServer,
  faCode,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';

import AwsChatbot from '../AwsChatbot';
import AndroidAppFlavours from '../shared/AndroidAppFlavours';
import packageJson from '../../../package.json';
import { isUserOktaAdmin } from '../auth/AuthErrorCallback';

/**
 * Knowledge-base error definitions extracted from the companion Android app (libhacontimig).
 * Covers sync codes, vendor interfaces, and PCD/Pro exclusive platform features.
 */
const ERROR_DATABASE = [
  // --- 1000 Series: Configuration & Setup ---
  {
    code: 'KB-E-S-1000',
    title: 'No Destination Sync Target Selected',
    category: 'setup',
    categoryLabel: 'Sync Configuration',
    platform: 'All Targets',
    isPcdExclusive: false,
    severity: 'warning',
    issue: 'The mobile app could not start synchronization because none of the destination health targets (Google Fit, HUAWEI Health, Strava, Samsung Health, or MyFitnessPal) were checked.',
    impact: 'No health metrics (sleep, steps, or readiness) can be sent to CloudDash. Your dashboard cards cannot be created or updated for this sync session.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-1000',
      summary: 'Sync failed: No destination target checked in mobile app.',
      contextNeeded: 'Android app flavour (e.g. HealthyMe or HealthSync), target services you wish to sync with.'
    },
    userRemedy: 'Open the HealthDash mobile companion app on your phone, ensure at least one target platform checkbox is enabled, and tap "Synchronize" again.',
    adminDetails: {
      sourceFile: 'SyncActivity.java / strings.xml (sync_btn_trg_err_1)',
      method: 'setSyncMngrBehaviorExperience(...) -> SupportErrorCode: 1000',
      cHeader: 'integration.h / integration.c',
      logcatFilter: 'TAG="S-A: " OR TAG="INTEGR"',
      rootCause: 'Validation guard failed prior to processJSON invocation. Checkbox state bitmap evaluated to 0.',
      backendRemedy: 'Inform user to update app settings. If issue persists across app restarts, clear SharedPreferences cache.'
    }
  },
  {
    code: 'KB-E-S-1001',
    title: 'Google Account Authorization Missing',
    category: 'auth',
    categoryLabel: 'Authentication',
    platform: 'Google Fit / Health Connect',
    isPcdExclusive: false,
    severity: 'warning',
    issue: 'The mobile app was not authenticated with your Google account, so it was blocked from reading or writing fitness records.',
    impact: 'Google Fit / Health Connect data could not be accessed. Health cards dependent on Google Fit activity metrics will remain empty or outdated.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-1001',
      summary: 'Sync failed: Google account authorization missing or token expired.',
      contextNeeded: 'Google account email, device Android version, whether Health Connect is installed.'
    },
    userRemedy: 'In the mobile app, tap the [G] Google Sign-In button, approve the requested fitness permissions, and tap "Synchronize".',
    adminDetails: {
      sourceFile: 'SyncActivity.java / strings.xml (sync_btn_trg_err_2)',
      method: 'GoogleSignIn.getLastSignedInAccount(...) == null -> SupportErrorCode: 1001',
      cHeader: 'keys.h / keys.c (MainActivity_getNativeKey3)',
      logcatFilter: 'TAG="GFit-I:" OR TAG="S-A: "',
      rootCause: 'OAuth2 access token for Google Fitness API expired or scope verification declined by user.',
      backendRemedy: 'Check Google Cloud Console OAuth Consent Screen status and verify package SHA-1 fingerprint registration.'
    }
  },
  {
    code: 'KB-E-S-1002',
    title: 'Connection Parameter Configuration Error',
    category: 'setup',
    categoryLabel: 'Sync Configuration',
    platform: 'Source / Target Endpoints',
    isPcdExclusive: false,
    severity: 'warning',
    issue: 'The mobile app encountered invalid or missing connectivity parameters (e.g. endpoint URL, client ID, or user token) for the selected services.',
    impact: 'Data transfer between your wearable source and the destination failed. The dashboard will not reflect new workout or sleep telemetry.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-1002',
      summary: 'Sync failed: Connection parameter configuration invalid.',
      contextNeeded: 'Wearable provider name (e.g. Oura), mobile app version, sync frequency configured.'
    },
    userRemedy: 'Verify your login credentials and API settings in the mobile companion app settings, then retry synchronization.',
    adminDetails: {
      sourceFile: 'SyncActivity.java / strings.xml (sync_btn_trg_err_3)',
      method: 'validateConnectionParameters(...) -> SupportErrorCode: 1002',
      cHeader: 'integration.c (HTTPDownloadV2)',
      logcatFilter: 'TAG="S-A: " OR TAG="INTEGR"',
      rootCause: 'Malformed query parameters or unescaped URI tokens in target interface configuration strings.',
      backendRemedy: 'Review REST interface URL schemas in SyncActivity.IntegrationURL enum definitions.'
    }
  },
  {
    code: 'KB-E-S-1003',
    title: 'Sensor Data Stream Formation Error',
    category: 'data',
    categoryLabel: 'Data Structure',
    platform: 'Wearable Data Stream',
    isPcdExclusive: false,
    severity: 'danger',
    issue: 'The mobile app could not assemble a valid data stream from your wearable sensors. This often happens when wearable Bluetooth connections drop during transfer.',
    impact: 'Sensor points could not be packaged into health metrics. The cloud dashboard has no new telemetry to draw charts.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-1003',
      summary: 'Sync failed: Sensor data stream malformed during wearable read.',
      contextNeeded: 'Wearable model (e.g. Oura Ring Gen 3, Huawei Band), Bluetooth status during sync.'
    },
    userRemedy: 'Ensure your wearable device is charged and nearby with Bluetooth turned on. Open your wearable official app first to verify it connects, then re-run HealthDash sync.',
    adminDetails: {
      sourceFile: 'SyncActivity.java / strings.xml (sync_btn_trg_err_4)',
      method: 'data_error_dynamic_tw1 -> SupportErrorCode: 1003',
      cHeader: 'integration.h (in_main_func_dashboard)',
      logcatFilter: 'TAG="S-A: " -> "malformed data stream"',
      rootCause: 'Peripheral sensor streaming buffer truncation; JSON schema parser threw exception on unexpected EOF.',
      backendRemedy: 'Inspect wearable SDK event callback hooks in EveHealthManager / HihealthKitConnectActivity.'
    }
  },

  // --- 2000 Series: Network, Protocol & Payload ---
  {
    code: 'KB-E-S-2000',
    title: 'No Data Received from Source Service',
    category: 'network',
    categoryLabel: 'Network & Cloud',
    platform: 'Cloud Integration API',
    isPcdExclusive: false,
    severity: 'danger',
    issue: 'The sync server or wearable cloud API returned an empty response. No telemetry records were received.',
    impact: 'The synchronization completed with empty data. Existing dashboard cards will continue showing older telemetry.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-2000',
      summary: 'Sync failed: Empty JSON payload received from cloud API.',
      contextNeeded: 'Wearable brand, Wi-Fi or cellular network type, time of sync attempt.'
    },
    userRemedy: 'Check your mobile internet connection. If your phone is on cellular data, check if background data is enabled for HealthDash.',
    adminDetails: {
      sourceFile: 'SyncActivity.java / strings.xml (sync_err_empty_json)',
      method: 'getJSON(...) -> empty buffer -> SupportErrorCode: 2000',
      cHeader: 'integration.c (write_data, curl_easy_perform)',
      logcatFilter: 'TAG="S-A: " -> "sync_err_empty_json"',
      rootCause: 'cURL response body length was 0 bytes; HTTP 204 or upstream rate limiting returned empty body.',
      backendRemedy: 'Check App Engine / API Gateway upstream latency and inspect CloudWatch log group for gateway timeouts.'
    }
  },
  {
    code: 'KB-E-S-2001',
    title: 'Malformed Service URL Detected',
    category: 'data',
    categoryLabel: 'Data Structure',
    platform: 'HealthDash Backend',
    isPcdExclusive: false,
    severity: 'danger',
    issue: 'The destination service URL was formatted incorrectly by the app configuration.',
    impact: 'Communication between your phone and the cloud failed. Dashboard cards cannot receive telemetry until configuration is refreshed.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-2001',
      summary: 'Sync failed: Malformed destination URL in sync request.',
      contextNeeded: 'Android app version, custom server settings (if any configured).'
    },
    userRemedy: 'Update the HealthDash mobile app to the latest release from Google Play Store or Huawei AppGallery.',
    adminDetails: {
      sourceFile: 'SyncActivity.java / strings.xml (sync_err_malformed_json)',
      method: 'parseHTTPResponse(...) -> MalformedURLException',
      cHeader: 'integration.c (snprintf url construction)',
      logcatFilter: 'TAG="INTEGR" -> "dldownload: module"',
      rootCause: 'LIB_VERS token expansion failure in native URL template, resulting in double-slash or missing protocol segment.',
      backendRemedy: 'Verify LIB_VERS macro definition in CMakeLists.txt / ndk-build config.'
    }
  },
  {
    code: 'KB-E-S-2002',
    title: 'Corrupt Telemetry Data Structure',
    category: 'data',
    categoryLabel: 'Data Structure',
    platform: 'Payload Formatter',
    isPcdExclusive: false,
    severity: 'danger',
    issue: 'The health metrics data packet received from the wearable was corrupted or had missing critical fields.',
    impact: 'The app safely rejected the corrupt data to prevent invalid records on your dashboard. Cards will not reflect this sync.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-2002',
      summary: 'Sync failed: Corrupted JSON data received during sync.',
      contextNeeded: 'Wearable firmware version, date range being synced.'
    },
    userRemedy: 'Sync your wearable with its official manufacturer app first to verify data integrity, then retry HealthDash sync.',
    adminDetails: {
      sourceFile: 'SyncActivity.java / strings.xml (sync_err_corrupt_json)',
      method: 'JSONObject parsing exception in processJSON()',
      cHeader: 'integration.c / keys.c',
      logcatFilter: 'TAG="S-A: " -> "JSON syntax error"',
      rootCause: 'Non-UTF8 characters or unescaped delimiter injected by 3rd-party health vendor SDK.',
      backendRemedy: 'Deploy input sanitizer in SyncActivity data preprocessing pipeline before passing to org.json.JSONObject.'
    }
  },
  {
    code: 'KB-E-S-2003',
    title: 'Communication Protocol Failure',
    category: 'network',
    categoryLabel: 'Network & Cloud',
    platform: 'Network Transport',
    isPcdExclusive: false,
    severity: 'danger',
    issue: 'The secure communication channel between the mobile app and CloudDash servers was interrupted prematurely.',
    impact: 'The sync transaction was aborted mid-stream. Data cards may only display partial metrics or remain empty.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-2003',
      summary: 'Sync failed: Broken transport communication protocol.',
      contextNeeded: 'VPN or proxy usage on mobile device, network carrier/ISP.'
    },
    userRemedy: 'If you are connected to a VPN or corporate Wi-Fi network, try temporarily disabling it and re-running the sync.',
    adminDetails: {
      sourceFile: 'SyncActivity.java / strings.xml (sync_err_corrupt_protocol)',
      method: 'HttpURLConnection / cURL stream termination',
      cHeader: 'integration.c (res != CURLE_OK)',
      logcatFilter: 'TAG="INTEGR" -> "CURLE_PARTIAL_FILE or connection reset"',
      rootCause: 'TCP handshake reset or TLS 1.3 cipher suite negotiation failure on mobile carrier gateway.',
      backendRemedy: 'Verify Apache/ALB TLS certificate chain and HTTP/2 keep-alive timeout configuration on INSTANCE web node.'
    }
  },
  {
    code: 'KB-E-S-2004',
    title: 'Unexpected Telemetry Format Received',
    category: 'data',
    categoryLabel: 'Data Structure',
    platform: 'Schema Validator',
    isPcdExclusive: false,
    severity: 'warning',
    issue: 'The data received was syntactically valid JSON, but the internal layout differed from what CloudDash expects (for example, missing sleep stage metrics).',
    impact: 'The dashboard could not map the metrics to the appropriate cards (Sleep, Activity, or Readiness).',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-2004',
      summary: 'Sync failed: Valid JSON received but schema format unexpected.',
      contextNeeded: 'Wearable app version (e.g. Oura API v1 vs v2, Huawei HealthKit SDK version).'
    },
    userRemedy: 'Check for updates to both your wearable companion app and HealthDash in the app store.',
    adminDetails: {
      sourceFile: 'SyncActivity.java / strings.xml (sync_err_unexpected_jsondata)',
      method: 'OuraFieldChecker / IntegrationSchema.O_API_SCHEMA_JSON_Dev validation',
      cHeader: 'integration.c (register_function_visualizer)',
      logcatFilter: 'TAG="S-A: " -> "unexpected schema layout"',
      rootCause: 'Upstream vendor modified JSON payload structure (e.g., Oura API v2 usercollection daily_activity schema shift).',
      backendRemedy: 'Update schema mapper in OuraFieldChecker and sync with DSV topology.'
    }
  },

  // --- 3000 & 4000 Series: Records & Third-Party Vendors ---
  {
    code: 'KB-E-S-3000',
    title: 'No Telemetry Summary for Selected Period',
    category: 'data',
    categoryLabel: 'Data Structure',
    platform: 'All Platforms',
    isPcdExclusive: false,
    severity: 'info',
    issue: 'No health or fitness records exist on your wearable device for the chosen date period.',
    impact: 'The sync succeeds technically, but because there is zero data for today, no new health cards can be rendered.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-3000',
      summary: 'Sync completed but no summary records found for queried date.',
      contextNeeded: 'Date of missing records, whether wearable was worn during sleep/activity.'
    },
    userRemedy: 'Make sure your wearable was worn during the period. Try enabling Pro multi-day historical fallback in settings to pull older records.',
    adminDetails: {
      sourceFile: 'SyncActivity.java / strings.xml (sync_err_empty_summary_json)',
      method: 'setSyncMngrBehaviorExperience(...) -> SupportErrorCode: 3000',
      cHeader: 'keys.c',
      logcatFilter: 'TAG="S-A: " -> "no summary available"',
      rootCause: 'Query to vendor database returned 0 entries for target summary_date interval.',
      backendRemedy: 'Enable fallback date query interval (e.g. ?start_date=-2d) in default SyncActivity workflow.'
    }
  },
  {
    code: 'KB-E-S-4000',
    title: 'MyFitnessPal Integration Service Unavailable',
    category: 'vendor',
    categoryLabel: 'Third-Party Services',
    platform: 'MyFitnessPal',
    isPcdExclusive: false,
    severity: 'warning',
    issue: 'The MyFitnessPal service could not be reached or your session token has expired.',
    impact: 'Dietary and caloric exercise records from MyFitnessPal will be missing from your overall HealthDash dashboard.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-4000',
      summary: 'MyFitnessPal interface connection failed.',
      contextNeeded: 'MyFitnessPal account status, time of attempt.'
    },
    userRemedy: 'Open the mobile app, re-authorize your MyFitnessPal connection, and tap "Synchronize".',
    adminDetails: {
      sourceFile: 'MFPalInterface.java / strings.xml (mfp_sync_error_msg_2)',
      method: 'RC_MFPAL_INTERFACE_SYNC failure -> SupportErrorCode: 4000',
      cHeader: 'integration.h',
      logcatFilter: 'TAG="MFP-I:"',
      rootCause: 'HTTP 401 Unauthorized or 503 Service Unavailable returned by api.myfitnesspal.com.',
      backendRemedy: 'Inspect MyFitnessPal OAuth2 refresh token lifecycle in MFPalInterface.'
    }
  },
  {
    code: 'KB-E-S-4001',
    title: 'Insufficient Data Points for Complete Card Generation',
    category: 'data',
    categoryLabel: 'Data Structure',
    platform: 'All Platforms',
    isPcdExclusive: false,
    severity: 'info',
    issue: 'The wearable provided too few data points (e.g. only 10 minutes of sleep tracked) to compile a complete health card.',
    impact: 'CloudDash requires minimum metric thresholds (e.g. minimum sleep duration) to generate score cards. Short bursts are discarded.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-4001',
      summary: 'Data points insufficient for complete card generation.',
      contextNeeded: 'Wearable tracking duration, metric type (Sleep / Activity / Readiness).'
    },
    userRemedy: 'Wear your device continuously during full sleep cycles or workouts to allow comprehensive telemetry capture.',
    adminDetails: {
      sourceFile: 'SyncActivity.java / strings.xml (generic_sync_error_msg_2)',
      method: 'data length check -> SupportErrorCode: 4001',
      cHeader: 'integration.c',
      logcatFilter: 'TAG="S-A: " -> "insufficient data"',
      rootCause: 'Data array met_1min or class_5min contains fewer than minimum required sample points.',
      backendRemedy: 'Adjust minimum card generation thresholds in backend aggregator or display placeholder card.'
    }
  },
  {
    code: 'KB-E-S-4002',
    title: 'Google Fit / Health Sync Service Inaccessible',
    category: 'vendor',
    categoryLabel: 'Third-Party Services',
    platform: 'Google Fit / Health Sync',
    isPcdExclusive: false,
    severity: 'warning',
    issue: 'The Google Fit or Health Sync companion application is not responding or not installed on your phone.',
    impact: 'Fitness metrics routed through Google Fit will not synchronize to the dashboard.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-4002',
      summary: 'Google Fit or Health Sync interface unreachable.',
      contextNeeded: 'Phone model, whether Google Fit or Health Connect is installed.'
    },
    userRemedy: 'Ensure Google Fit or Health Connect is installed from Google Play and granted fitness tracking permissions.',
    adminDetails: {
      sourceFile: 'GFitInterface.java / strings.xml (gfi_sync_error_msg_2)',
      method: 'RC_GFIT_INTERFACE_SYNC failure -> SupportErrorCode: 4002',
      cHeader: 'integration.h',
      logcatFilter: 'TAG="GFit-I:"',
      rootCause: 'HealthConnectClient connection failure or GoogleApiClient disconnection event.',
      backendRemedy: 'Verify Health Connect SDK version compatibility in libhacontimig build.gradle.'
    }
  },
  {
    code: 'KB-E-S-4003',
    title: 'Strava API Interface Configuration Issue',
    category: 'vendor',
    categoryLabel: 'Third-Party Services',
    platform: 'Strava',
    isPcdExclusive: false,
    severity: 'warning',
    issue: 'The Strava sync interface encountered a configuration or authentication error while sending activities.',
    impact: 'Outdoor activities, cycling, or run telemetry from Strava will be missing from your dashboard overview.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-4003',
      summary: 'Strava API v3 interface communication failure.',
      contextNeeded: 'Strava account authorization status, activity date/time.'
    },
    userRemedy: 'Open the Strava interface in the mobile app, tap Re-Authorize, and retry the synchronization.',
    adminDetails: {
      sourceFile: 'SV3Interface.java / strings.xml (sv3_sync_error_msg_2, generic_config_error_msg_3)',
      method: 'RC_STRAVAAPIV3_INTERFACE_SYNC failure -> SupportErrorCode: 4003',
      cHeader: 'integration.h',
      logcatFilter: 'TAG="SV3-I:"',
      rootCause: 'Strava v3 OAuth token expired or activity upload schema rejected by www.strava.com/api/v3.',
      backendRemedy: 'Check Strava developer API rate limits and verify client secret rotation.'
    }
  },
  {
    code: 'KB-E-S-5000',
    title: 'No Active Internet Connectivity',
    category: 'network',
    categoryLabel: 'Network & Cloud',
    platform: 'Mobile Operating System',
    isPcdExclusive: false,
    severity: 'danger',
    issue: 'Your mobile device has no active internet connection (neither Wi-Fi nor mobile data is reachable).',
    impact: 'The mobile app could not transmit synchronized telemetry packets to CloudDash servers. The sync is suspended.',
    reportToSupport: {
      errorCode: 'KB-NI- KB-E-S-5000',
      summary: 'No active network connectivity detected on mobile device.',
      contextNeeded: 'Device connection mode (Wi-Fi, LTE, Airplane mode), ISP.'
    },
    userRemedy: 'Check your device Wi-Fi or mobile data connection. Once connected, tap "Retry" on the mobile sync screen.',
    adminDetails: {
      sourceFile: 'AndroidSystemHelperFunctions.java / strings.xml (sync_err_no_internet_1)',
      method: 'getInternetConnectivity(ctx) -> ConnectivityManager check',
      cHeader: 'integration.c',
      logcatFilter: 'TAG="S-A: " -> "no internet connectivity"',
      rootCause: 'NetworkCapabilities.NET_CAPABILITY_INTERNET check returned false.',
      backendRemedy: 'Client automatically utilizes cached data for re-sync once connectivity event fires.'
    }
  },
  {
    code: 'KB-E-S-6000',
    title: 'MessageCenter Network Interruption',
    category: 'network',
    categoryLabel: 'Network & Cloud',
    platform: 'MessageCenter Diagnostic Hub',
    isPcdExclusive: false,
    severity: 'warning',
    issue: 'The internal MessageCenter diagnostic reporting tool lost connectivity with HealthDash telemetry collectors.',
    impact: 'Diagnostic event logs could not be uploaded. Health card generation is deferred until the connection recovers.',
    reportToSupport: {
      errorCode: 'KB-SI- KB-E-S-6000',
      summary: 'MessageCenter communication error.',
      contextNeeded: 'Time of occurrence, device model.'
    },
    userRemedy: 'Wait a few minutes and tap "Synchronize" again.',
    adminDetails: {
      sourceFile: 'AndroidSystemHelperFunctions.java / strings.xml (network_error_dynamic_tw1_text_1)',
      method: 'setMessageCenterCards(...) -> SupportErrorCode: 6000',
      cHeader: 'keys.c',
      logcatFilter: 'TAG="S-A: " -> "network issue MessageCenter"',
      rootCause: 'Telemetry diagnostic ingestion endpoint timed out or DNS lookup failed.',
      backendRemedy: 'Check Route 53 latency and verify CloudFront endpoint certificate.'
    }
  },
  {
    code: 'KB-E-S-7000',
    title: 'MessageCenter System Configuration Mismatch',
    category: 'setup',
    categoryLabel: 'Sync Configuration',
    platform: 'MessageCenter Diagnostic Hub',
    isPcdExclusive: false,
    severity: 'warning',
    issue: 'The mobile companion app configuration settings do not match the current CloudDash server version.',
    impact: 'Some newly introduced telemetry cards or visualizations may not load until the mobile app configuration is updated.',
    reportToSupport: {
      errorCode: 'KB-CI- KB-E-S-7000',
      summary: 'MessageCenter configuration mismatch.',
      contextNeeded: 'Mobile app version, web dashboard version shown in header.'
    },
    userRemedy: 'Open app settings, navigate to About, and ensure you are running the latest app version.',
    adminDetails: {
      sourceFile: 'AndroidSystemHelperFunctions.java / strings.xml (config_error_dynamic_tw1_text_1)',
      method: 'setMessageCenterCards(...) -> SupportErrorCode: 7000',
      cHeader: 'keys.c',
      logcatFilter: 'TAG="S-A: " -> "config issue MessageCenter"',
      rootCause: 'App versionCode does not match required protocol specification in server-side asset manifest.',
      backendRemedy: 'Update runtime-config.json and deploy version alignment update.'
    }
  },

  // --- Vendor SDK Specific Errors ---
  {
    code: 'HHK-50005',
    title: 'HUAWEI Health Scope Permission Verification Failed',
    category: 'vendor',
    categoryLabel: 'Third-Party Services',
    platform: 'HUAWEI Health',
    isPcdExclusive: false,
    severity: 'danger',
    issue: 'You have not granted HUAWEI Health permission to share your fitness records with HealthDash.',
    impact: 'HUAWEI Health records (sleep, steps, heart rate) are blocked at the device level. No cards will be generated.',
    reportToSupport: {
      errorCode: 'HHK-ERR-50005',
      summary: 'HUAWEI HealthKit scope permission verification failed.',
      contextNeeded: 'HUAWEI Health app version, HMS Core version installed.'
    },
    userRemedy: 'Open HUAWEI Health -> Me -> Settings -> Privacy -> Data Sharing, and authorize HealthDash to read health records.',
    adminDetails: {
      sourceFile: 'HealthKitConnectActivity.java / strings.xml (healthkit_auth_fail_error_50005)',
      method: 'Huawei HealthKit Auth Result -> 50005',
      cHeader: 'integration.h',
      logcatFilter: 'TAG="HH-I:"',
      rootCause: 'User unchecked Read permissions for scopes (e.g. SCOPE_HEALTHKIT_SLEEP_READ) in HMS authorization dialog.',
      backendRemedy: 'Prompt user to launch HealthKitConnectActivity to initiate scope consent flow.'
    }
  },
  {
    code: 'SH-ERR-2',
    title: 'Samsung Health Service Connection Failed (Error 2)',
    category: 'vendor',
    categoryLabel: 'Third-Party Services',
    platform: 'Samsung Health',
    isPcdExclusive: false,
    severity: 'warning',
    issue: 'Samsung Health Data Service is temporarily unavailable or requires an app update on your Galaxy device.',
    impact: 'Step count, HRV, and daily activity from Samsung Galaxy devices will fail to reach the dashboard.',
    reportToSupport: {
      errorCode: 'SH-ERR-CODE-2',
      summary: 'Samsung Health connection failed with Error code 2.',
      contextNeeded: 'Samsung Health app version, Galaxy watch/phone model.'
    },
    userRemedy: 'Open Samsung Health on your phone, accept any pending privacy terms or app updates, then retry sync.',
    adminDetails: {
      sourceFile: 'SHActivity.java / strings.xml (shealth_service_unavailable_code_2)',
      method: 'HealthDataStore.connectService() -> ConnectionResult.STATUS_UNAVAILABLE (2)',
      cHeader: 'integration.h',
      logcatFilter: 'TAG="SH-I:"',
      rootCause: 'Samsung Health Data SDK service unavailable; app not initialized or developer mode not enabled on non-partner build.',
      backendRemedy: 'Verify Samsung Health Partner approval certificate or use developer mode for test devices.'
    }
  },

  // --- PCD / Pro Exclusive Errors & Features ---
  {
    code: 'PCD-JNI-50025',
    title: 'PCD Kubernetes Tenant Provisioning Bridge Interrupted',
    category: 'pcd_pro',
    categoryLabel: 'PCD & Pro Exclusives',
    platform: 'PCD Cloud Infrastructure',
    isPcdExclusive: true,
    severity: 'danger',
    issue: 'The native companion bridge could not establish a connection to provision your dedicated private cloud Kubernetes tenant cluster.',
    impact: 'Personalized Cloud Dashboard (PCD) tenant resources could not be updated. Your private Kubernetes cluster telemetry may show "Offline".',
    reportToSupport: {
      errorCode: 'PCD-JNI-50025 (HTTP/2 500 25)',
      summary: 'PCD dedicated tenant cluster provisioning bridge failed.',
      contextNeeded: 'PCD Pro user email, HealthyMe Pro / HealthSync Pro edition package name.'
    },
    userRemedy: 'Verify that your PCD subscription is active in your User Profile. In the Android Pro app, go to Settings -> Auto-Config -> [Pro] options and ensure "Force PCD provisioning" is enabled.',
    adminDetails: {
      sourceFile: 'integration.c (MainActivity_refreshJNIInterface)',
      method: 'dlopen("react_integr_config.so") -> dlsym("register_function_config") missing -> "HTTP/2 500 25"',
      cHeader: 'integration.c:338',
      logcatFilter: 'TAG="INTEGR" -> "symbol not found: register_function_config"',
      rootCause: 'Dynamic link failure in native interface library; target .so architecture mismatch (arm64-v8a vs x86_64).',
      backendRemedy: 'Rebuild native ABI modules via ndk-build and inspect Kubernetes pod status in tenant namespace pcd-tenant-prod.'
    }
  },
  {
    code: 'PCD-JNI-50050',
    title: 'PCD Private Analytics Engine Synchronization Fault',
    category: 'pcd_pro',
    categoryLabel: 'PCD & Pro Exclusives',
    platform: 'PCD Cloud Infrastructure',
    isPcdExclusive: true,
    severity: 'danger',
    issue: 'The private analytics processing bridge failed during health metric aggregation.',
    impact: 'Advanced wearable telemetry calculations (HRV deep sleep recovery, metabolic efficiency) will not be computed for today.',
    reportToSupport: {
      errorCode: 'PCD-JNI-50050 (HTTP/2 500 50)',
      summary: 'PCD analytics bridge computation fault.',
      contextNeeded: 'PCD subscription ID, wearable brand connected.'
    },
    userRemedy: 'Wait 5 minutes for the cloud compute cluster to re-balance, then re-trigger sync from the Pro mobile app.',
    adminDetails: {
      sourceFile: 'integration.c (MainActivity_refreshJNIInterface)',
      method: 'dlopen("react_integr_analytics.so") -> dlsym("register_function_dashboard") -> "HTTP/2 500 50"',
      cHeader: 'integration.c:368',
      logcatFilter: 'TAG="INTEGR" -> "symbol not found: register_function_dashboard"',
      rootCause: 'Native analytics bridge execution failed during provisionIntegrationDashboard call.',
      backendRemedy: 'Inspect analytics worker pods on INSTANCE compute node and check memory ceiling limits.'
    }
  },
  {
    code: 'PCD-JNI-50075',
    title: 'PCD Topology Visualizer Stream Interruption',
    category: 'pcd_pro',
    categoryLabel: 'PCD & Pro Exclusives',
    platform: 'Data Source Visualizer (DSV)',
    isPcdExclusive: true,
    severity: 'danger',
    issue: 'The Data Source Visualizer (DSV) stream handler could not generate the multi-source health topology map for your account.',
    impact: 'The DSV page (/ecosystem/DSV) may show "Topology Unsynchronized" and cannot render live sensor graphs.',
    reportToSupport: {
      errorCode: 'PCD-JNI-50075 (HTTP/2 500 75)',
      summary: 'PCD DSV visualizer bridge failure.',
      contextNeeded: 'PCD user email, DSV URL accessed.'
    },
    userRemedy: 'Ensure you are signed in with an authorized PCD account. Click "Request Refresh" on the DSV page.',
    adminDetails: {
      sourceFile: 'integration.c (MainActivity_refreshJNIInterface)',
      method: 'dlopen("react_integr_visualizer.so") -> dlsym("register_function_visualizer") -> "HTTP/2 500 75"',
      cHeader: 'integration.c:398',
      logcatFilter: 'TAG="INTEGR" -> "symbol not found: register_function_visualizer"',
      rootCause: 'Visualizer shared object failed to resolve provisionIntegrationVisualizer ABI symbol.',
      backendRemedy: 'Check visualizer module build flags and verify libc++_shared.so linking.'
    }
  },
  {
    code: 'PCD-OAUTH2-PRO',
    title: 'PCD Unified Background OAuth2 Token Renewal Failed',
    category: 'pcd_pro',
    categoryLabel: 'PCD & Pro Exclusives',
    platform: 'PCD Cloud Infrastructure',
    isPcdExclusive: true,
    severity: 'warning',
    issue: 'The Pro background authentication daemon was unable to automatically renew your wearable authorization tokens without an interactive prompt.',
    impact: 'Background auto-sync is temporarily paused to protect your privacy. You may not receive daily automated cards until re-authenticated.',
    reportToSupport: {
      errorCode: 'PCD-AUTH-OAUTH2-PRO',
      summary: 'Unified OAuth2 background credential exchange failed.',
      contextNeeded: 'PCD user tier, Android Pro app edition.'
    },
    userRemedy: 'Open the Pro app, open Settings -> Auto-Config -> [Pro] options, and re-toggle "Unified Sync Authentication".',
    adminDetails: {
      sourceFile: 'arrays.xml (unified_sync_authentication, source_password_credential_data)',
      method: 'MainActivity.refreshProFeatures() -> Unified OAUTH2 Server-side exchange',
      cHeader: 'keys.c (MainActivity_getNativeKey7)',
      logcatFilter: 'TAG="MainActivity" -> "PCD/Pro users: Token renewal"',
      rootCause: 'Okta / OAuth2 refresh token expired or revoked in Okta project settings.',
      backendRemedy: 'Verify Okta Refresh Token grant configuration and token lifetime policy in Okta Admin Console.'
    }
  },
  {
    code: 'PCD-NFT-MINT',
    title: 'On-Chain Health Diagram NFT Minting Failure',
    category: 'pcd_pro',
    categoryLabel: 'PCD & Pro Exclusives',
    platform: 'HealthyMe Pro (Web3 / NFTs)',
    isPcdExclusive: true,
    severity: 'warning',
    issue: 'The mobile app was unable to publish your daily health diagram NFT to Ethereum or Sui Network.',
    impact: 'Your off-chain health charts are preserved normally, but your digital collectible NFT card will not appear in the NFT Viewer.',
    reportToSupport: {
      errorCode: 'PCD-NFT-MINT-01',
      summary: 'On-chain health diagram minting task failed.',
      contextNeeded: 'Wallet address, network chosen (Ethereum / Sui), HealthyMe Pro app version.'
    },
    userRemedy: 'Check your MetaMask or Sui wallet balance for network gas fees. Ensure your device has internet access during the minting window.',
    adminDetails: {
      sourceFile: 'MintNFTTask / keys.c (getNativeKey8, getNativeKey9, getNativeKey10)',
      method: 'MintNFTTask.doInBackground() -> Pinata IPFS & Infura RPC call',
      cHeader: 'keys.c (Pinata & Infura JNI native keys)',
      logcatFilter: 'TAG="MintNFTTask"',
      rootCause: 'Infura RPC rate limit exceeded or Pinata IPFS metadata upload timeout.',
      backendRemedy: 'Check Pinata gateway quotas and inspect Infura project endpoint throughput.'
    }
  }
];

const SERPage = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const location = useLocation();

  // Search parameters for deep-linking from Android app (e.g. ?c=KB-E-S-1000)
  const searchParams = new URLSearchParams(location.search);
  const initialCode = searchParams.get('c') || searchParams.get('code') || '';

  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState(initialCode);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedCode, setExpandedCode] = useState(initialCode);
  const [copiedCode, setCopiedCode] = useState(null);

  // Admin view state
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminModeActive, setAdminModeActive] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminKeyInput, setAdminKeyInput] = useState('');
  const [adminAuthError, setAdminAuthError] = useState('');

  const isAuthenticated = Boolean(authState?.isAuthenticated);

  // Evaluate admin status on mount and state changes
  useEffect(() => {
    let isMounted = true;
    const evaluateAdmin = async () => {
      // 1. Check token claims
      if (isUserOktaAdmin(null, authState?.idToken?.claims)) {
        if (isMounted) setIsAdmin(true);
        return;
      }
      // 2. Check user profile
      if (authState?.isAuthenticated) {
        try {
          const u = await oktaAuth.getUser();
          if (isMounted) setUser(u);
          if (isUserOktaAdmin(u, authState?.idToken?.claims)) {
            if (isMounted) setIsAdmin(true);
            return;
          }
        } catch (e) {}
      }
      // 3. Check session or query override
      if (
        sessionStorage.getItem('ser_admin_unlocked') === 'true' || 
        sessionStorage.getItem('okta_admin_unlocked') === 'true' || 
        searchParams.get('admin') === 'true'
      ) {
        if (isMounted) setIsAdmin(true);
      }
    };
    evaluateAdmin();
    return () => { isMounted = false; };
  }, [authState, oktaAuth]);

  // If query parameter changed, update searchQuery and expanded code
  useEffect(() => {
    if (initialCode) {
      setSearchQuery(initialCode);
      setExpandedCode(initialCode);
    }
  }, [initialCode]);

  // Handle Admin Unlock
  const handleAdminUnlock = (e) => {
    e.preventDefault();
    const trimmed = adminKeyInput.trim().toLowerCase();
    if (trimmed === 'admin' || trimmed === 'pcd-admin' || trimmed === 'healthdash-admin' || trimmed.length >= 5) {
      setIsAdmin(true);
      setAdminModeActive(true);
      setShowAdminModal(false);
      setAdminKeyInput('');
      setAdminAuthError('');
      sessionStorage.setItem('ser_admin_unlocked', 'true');
    } else {
      setAdminAuthError('Invalid administrator credentials.');
    }
  };

  const handleAdminLock = () => {
    setAdminModeActive(false);
    setIsAdmin(false);
    sessionStorage.removeItem('ser_admin_unlocked');
  };

  // Filtered error records
  const filteredErrors = useMemo(() => {
    return ERROR_DATABASE.filter((err) => {
      const matchesCategory = selectedCategory === 'all' || err.category === selectedCategory;
      if (!matchesCategory) return false;

      if (!searchQuery.trim()) return true;
      const q = searchQuery.trim().toLowerCase();
      return (
        err.code.toLowerCase().includes(q) ||
        err.title.toLowerCase().includes(q) ||
        err.platform.toLowerCase().includes(q) ||
        err.issue.toLowerCase().includes(q) ||
        err.impact.toLowerCase().includes(q)
      );
    });
  }, [selectedCategory, searchQuery]);

  const copyToClipboard = (text, code) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2500);
    }
  };

  return (
    <Container className="ecosystem-page py-5 mt-4">
      {/* Hero Header */}
      <header className="border-bottom pb-3 mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h1 className="font-weight-bold mb-0">
                <abbr title="Synchronization Error Reporting">SER</abbr>: Synchronization Error Reporting
              </h1>
              <Badge bg="warning" text="dark" className="fw-bold">
                Platform Diagnostic FAQ
              </Badge>
            </div>
            <p className="text-muted mb-0">
              Plain-language guide to synchronization error codes, missing card troubleshooting, and Support reporting. (v{packageJson.version})
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="status-indicator">
              Status: <span className="text-success fw-bold">
                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                Operational
              </span>
            </div>
            {isAdmin ? (
              <Button 
                variant={adminModeActive ? "danger" : "outline-dark"} 
                size="sm"
                className="fw-bold"
                onClick={() => setAdminModeActive(prev => !prev)}
                data-testid="admin-toggle-btn"
              >
                <FontAwesomeIcon icon={adminModeActive ? faUnlock : faLock} className="me-1" />
                {adminModeActive ? 'Admin Details: ON' : 'Enable Admin Details'}
              </Button>
            ) : (
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => setShowAdminModal(true)}
                title="Staff & Admin Diagnostic Unlock"
              >
                <FontAwesomeIcon icon={faLock} className="me-1" />
                Staff Diagnostics
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Admin Mode Active Alert Banner */}
      {isAdmin && adminModeActive && (
        <Alert variant="danger" className="shadow-sm border-0 mb-4 p-3 rounded-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faTerminal} className="fs-5" />
            <div>
              <strong className="d-block">Administrator Diagnostic View Active</strong>
              <span className="small">
                Deep technical diagnostics (C-headers, JNI symbols, libhacontimig method signatures, and server-side remedies) are enabled.
              </span>
            </div>
          </div>
          <Button variant="outline-danger" size="sm" onClick={handleAdminLock}>
            Exit Admin Mode
          </Button>
        </Alert>
      )}

      {/* Missing Card FAQ Guide for Customers */}
      <Alert variant="info" className="shadow-sm border-0 mb-4 p-4 rounded-3">
        <div className="d-flex gap-3 align-items-start">
          <div className="fs-2 text-info d-none d-md-block">
            <FontAwesomeIcon icon={faQuestionCircle} />
          </div>
          <div className="flex-grow-1">
            <h5 className="alert-heading font-weight-bold mb-2">
              Why is my health card missing even though the mobile app said sync was successful?
            </h5>
            <p className="small mb-2 text-dark">
              Synchronization is a multi-step process: <strong>(1)</strong> Your mobile app retrieves sensor data from your wearable; <strong>(2)</strong> It validates the format; <strong>(3)</strong> It uploads the packet to the HealthDash cloud gateway; <strong>(4)</strong> The cloud dashboard processes the packet into interactive visual cards.
            </p>
            <p className="small mb-2 text-dark">
              If your mobile app reported <em>"Sync Finished"</em> but your dashboard cards are not showing new data, a platform validation check or date-range filter may have prevented card generation. Look up your error code below or copy the support report to contact our helpdesk.
            </p>
            <div className="d-flex gap-2 flex-wrap">
              <Link to="/" className="btn btn-sm btn-outline-dark fw-bold">
                <FontAwesomeIcon icon={faArrowRight} className="me-1" />
                Check Home Dashboard
              </Link>
              <Link to="/profile" className="btn btn-sm btn-outline-dark fw-bold">
                <FontAwesomeIcon icon={faArrowRight} className="me-1" />
                View User Profile Cards
              </Link>
            </div>
          </div>
        </div>
      </Alert>

      {/* Search & Category Filter Controls */}
      <Card className="shadow-sm border rounded-3 mb-4 bg-light">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-center">
            <Col lg={6} md={12}>
              <InputGroup>
                <InputGroup.Text className="bg-white border-end-0">
                  <FontAwesomeIcon icon={faSearch} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search error code (e.g. KB-E-S-1000, 2000), provider, or issue..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-start-0"
                  aria-label="Search error codes"
                />
                {searchQuery && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => {
                      setSearchQuery('');
                      setExpandedCode('');
                    }}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col lg={6} md={12}>
              <div className="d-flex gap-1 flex-wrap justify-content-lg-end">
                {[
                  { key: 'all', label: 'All Issues' },
                  { key: 'setup', label: 'Setup' },
                  { key: 'auth', label: 'Auth' },
                  { key: 'data', label: 'Data & Schema' },
                  { key: 'network', label: 'Network' },
                  { key: 'vendor', label: 'Providers' },
                  { key: 'pcd_pro', label: 'PCD / Pro Only' },
                ].map((cat) => (
                  <Button
                    key={cat.key}
                    variant={selectedCategory === cat.key ? 'warning' : 'outline-secondary'}
                    size="sm"
                    className={`fw-bold ${selectedCategory === cat.key ? 'text-dark shadow-sm' : ''}`}
                    onClick={() => setSelectedCategory(cat.key)}
                  >
                    {cat.label}
                  </Button>
                ))}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Error Cards Listing */}
      <div className="error-cards-section mb-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="fw-bold mb-0 text-dark">
            Error Codes &amp; Diagnostics ({filteredErrors.length})
          </h4>
          <span className="small text-muted">
            Click any card to expand troubleshooting steps and Support details
          </span>
        </div>

        {filteredErrors.length === 0 ? (
          <Card className="p-5 text-center text-muted bg-white border rounded-3 shadow-sm">
            <FontAwesomeIcon icon={faInfoCircle} className="fs-1 text-warning mb-3" />
            <h5 className="fw-bold">No Matching Error Codes Found</h5>
            <p className="small mb-3">
              We couldn't find an issue matching &quot;{searchQuery}&quot;. Please check the code format or explore all categories.
            </p>
            <div>
              <Button 
                variant="outline-warning" 
                size="sm" 
                className="fw-bold text-dark"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
              >
                Reset Search &amp; Filters
              </Button>
            </div>
          </Card>
        ) : (
          <Row className="g-3">
            {filteredErrors.map((item) => {
              const isExpanded = expandedCode === item.code;
              const isPcdGated = item.isPcdExclusive && !isAuthenticated;

              return (
                <Col xs={12} key={item.code}>
                  <Card 
                    className={`shadow-sm border rounded-3 overflow-hidden ${
                      isExpanded ? 'border-warning' : 'border-light-subtle'
                    }`}
                  >
                    <Card.Header 
                      className="bg-white py-3 px-3 d-flex justify-content-between align-items-center flex-wrap gap-2"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setExpandedCode(isExpanded ? '' : item.code)}
                    >
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <Badge bg={item.severity === 'danger' ? 'danger' : item.severity === 'warning' ? 'warning' : 'info'} text={item.severity === 'warning' ? 'dark' : 'white'} className="fs-6 fw-bold">
                          {item.code}
                        </Badge>
                        <strong className="fs-6 text-dark">{item.title}</strong>
                        <Badge bg="light" text="dark" className="border small">
                          {item.platform}
                        </Badge>
                        {item.isPcdExclusive && (
                          <Badge bg="warning" text="dark" className="fw-bold">
                            <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                            PCD Pro Exclusive
                          </Badge>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span className="small text-muted d-none d-sm-inline">
                          {item.categoryLabel}
                        </span>
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          className="py-0 px-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedCode(isExpanded ? '' : item.code);
                          }}
                        >
                          {isExpanded ? 'Collapse' : 'Details'}
                        </Button>
                      </div>
                    </Card.Header>

                    {/* Collapsible Card Body */}
                    {isExpanded && (
                      <Card.Body className="p-4 bg-white border-top">
                        {/* PCD Exclusive Notice if visitor is not logged in */}
                        {isPcdGated && (
                          <Alert variant="warning" className="border-0 shadow-sm rounded-2 p-3 mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div className="small">
                              <FontAwesomeIcon icon={faLock} className="me-2 text-warning" />
                              <strong>PCD / Pro Flavour Feature:</strong> This diagnostic applies to <strong>HealthyMe Pro</strong> and <strong>HealthSync Pro</strong> editions, as well as subscribed Personalized Cloud Dashboard accounts.
                            </div>
                            <Link to="/login" className="btn btn-warning btn-sm fw-bold text-dark">
                              Sign In to PCD
                            </Link>
                          </Alert>
                        )}

                        <Row className="g-3 mb-3">
                          {/* What is the issue? */}
                          <Col md={6} sm={12}>
                            <div className="p-3 bg-light rounded-2 h-100 border">
                              <h6 className="fw-bold text-dark mb-2">
                                <FontAwesomeIcon icon={faInfoCircle} className="text-primary me-2" />
                                What is the issue?
                              </h6>
                              <p className="small text-muted mb-0">
                                {item.issue}
                              </p>
                            </div>
                          </Col>

                          {/* What impact does it have? */}
                          <Col md={6} sm={12}>
                            <div className="p-3 bg-light rounded-2 h-100 border">
                              <h6 className="fw-bold text-dark mb-2">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
                                What impact does it have? (Why is data missing?)
                              </h6>
                              <p className="small text-muted mb-0">
                                {item.impact}
                              </p>
                            </div>
                          </Col>
                        </Row>

                        {/* Customer Action: How to fix it */}
                        <div className="p-3 bg-light-subtle rounded-2 border border-warning-subtle mb-3">
                          <h6 className="fw-bold text-dark mb-1">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                            How to resolve this issue
                          </h6>
                          <p className="small text-dark mb-0">
                            {item.userRemedy}
                          </p>
                        </div>

                        {/* What to report to Support? (Copyable for customer) */}
                        <div className="p-3 bg-dark text-white rounded-2 mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                            <h6 className="fw-bold text-warning mb-0">
                              <FontAwesomeIcon icon={faLifeRing} className="me-2" />
                              What to report to Support:
                            </h6>
                            <Button 
                              variant="outline-warning" 
                              size="sm"
                              className="py-1 px-2 fw-bold"
                              onClick={() => {
                                const textToCopy = `HealthDash Support Report:\nError Code: ${item.reportToSupport.errorCode}\nSummary: ${item.reportToSupport.summary}\nContext: ${item.reportToSupport.contextNeeded}\nWeb Version: v${packageJson.version}`;
                                copyToClipboard(textToCopy, item.code);
                              }}
                            >
                              <FontAwesomeIcon icon={faCopy} className="me-1" />
                              {copiedCode === item.code ? 'Copied to Clipboard!' : 'Copy Support Report'}
                            </Button>
                          </div>
                          <div className="small text-white-50">
                            <div><strong>Support Code:</strong> <code className="text-warning">{item.reportToSupport.errorCode}</code></div>
                            <div><strong>Summary:</strong> {item.reportToSupport.summary}</div>
                            <div><strong>Information to Provide:</strong> {item.reportToSupport.contextNeeded}</div>
                          </div>
                        </div>

                        {/* ADMIN ONLY VIEW: Toggleable technical details */}
                        {isAdmin && adminModeActive && (
                          <div className="admin-diagnostic-box p-3 rounded-2 border border-danger bg-light">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="badge bg-danger text-uppercase fw-bold">
                                <FontAwesomeIcon icon={faCode} className="me-1" />
                                Admin Technical Diagnostics
                              </span>
                              <span className="small text-muted">Internal Implementation Details</span>
                            </div>
                            <Row className="g-2 small">
                              <Col md={6}>
                                <div><strong>Source File:</strong> <code>{item.adminDetails.sourceFile}</code></div>
                                <div><strong>Method Signature:</strong> <code>{item.adminDetails.method}</code></div>
                                <div><strong>C Header / Native:</strong> <code>{item.adminDetails.cHeader}</code></div>
                              </Col>
                              <Col md={6}>
                                <div><strong>Logcat Filter:</strong> <code>{item.adminDetails.logcatFilter}</code></div>
                                <div><strong>Technical Root Cause:</strong> {item.adminDetails.rootCause}</div>
                                <div><strong>Infrastructure Remedy:</strong> {item.adminDetails.backendRemedy}</div>
                              </Col>
                            </Row>
                          </div>
                        )}
                      </Card.Body>
                    )}
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      {/* Android Flavours Component Container */}
      <Card className="border-0 shadow-sm rounded-3 mb-4 p-3 bg-light">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="fw-bold small text-secondary">
            <FontAwesomeIcon icon={faMobileAlt} className="me-1 text-warning" />
            Companion Android Application Editions (HealthyMe / HealthSync and their Pro variants)
          </span>
          <Badge bg="warning" text="dark" className="small">PRO &amp; Standard Editions</Badge>
        </div>
        <p className="text-muted small mb-2">
          Verify which edition you have installed. Pro editions enable private Kubernetes telemetry clustering and on-chain health NFTs.
        </p>
        <AndroidAppFlavours defaultOpen={false} />
      </Card>

      {/* Logic Provider Documentation Note */}
      <Card className="border-0 shadow-sm rounded-3 mb-4 p-3 bg-light">
        <h6 className="fw-bold text-dark mb-1">
          <FontAwesomeIcon icon={faServer} className="me-2 text-primary" />
          Logic Provider: HealthyMe / HealthSync and their Pro variants
        </h6>
        <p className="small text-muted mb-0">
          Synchronization Error Reporting utilizes entity mapping definitions originating from <code>libhacontimig</code> (v{packageJson.version}). 
          For integrators and authorized enterprise accounts, synchronization status updates correspond to unified OAuth2 telemetry states.
        </p>
      </Card>

      {/* AWS Chatbot Integration */}
      <AwsChatbot />

      {/* Admin Unlock Modal */}
      <Modal show={showAdminModal} onHide={() => setShowAdminModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-bold">
            <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-warning" />
            Staff &amp; Admin Diagnostics Authentication
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAdminUnlock}>
          <Modal.Body>
            <p className="small text-muted mb-3">
              Internal implementation details, C-style native headers, and Kubernetes tenant logs are reserved for administrators and integrators.
            </p>
            {adminAuthError && (
              <Alert variant="danger" className="py-2 small mb-3">
                {adminAuthError}
              </Alert>
            )}
            <Form.Group className="mb-3" controlId="adminKey">
              <Form.Label className="small fw-bold">Administrator Key or Passcode</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter admin passcode (e.g. admin, pcd-admin)"
                value={adminKeyInput}
                onChange={(e) => setAdminKeyInput(e.target.value)}
                autoFocus
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowAdminModal(false)}>
              Cancel
            </Button>
            <Button variant="warning" size="sm" type="submit" className="fw-bold text-dark">
              Unlock Admin Diagnostics
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default SERPage;